import * as React from "react";
import {
  CalendarCheck,
  CircleDollarSign,
  HandCoins,
  Loader2,
  MoreHorizontal,
  UserPlus,
  Users,
  Wallet,
} from "lucide-react";
import {
  Button,
  DataTable,
  DataTableEmpty,
  DataTableFooter,
  DataTablePagination,
  DataTableScroll,
  DataTableSkeleton,
  DataTableToolbar,
  DataTableToolbarSpacer,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  StatCard,
  StatusPill,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from "@eduviaos/ui-kit";
import { usePageSizePreference } from "@/hooks/usePageSizePreference";
import {
  PAY_BASIS_LABELS,
  formatPaise,
  formatPaiseCompact,
  monthLabel,
  useCloseMonth,
  useTeamMembers,
  useTeamPaySummary,
  type SummaryRow,
  type TeamMember,
} from "@/lib/teamPay/api";
import { AdjustmentSheet } from "@/components/team-pay/AdjustmentSheet";
import { AdvanceSheet } from "@/components/team-pay/AdvanceSheet";
import { MemberTermsSheet } from "@/components/team-pay/MemberTermsSheet";
import { PaymentSheet } from "@/components/team-pay/PaymentSheet";
import { StatementSheet } from "@/components/team-pay/StatementSheet";
import { ErrorBanner } from "@/components/team-pay/shared";
import { errorMessage, useTeamWords } from "@/lib/teamPay/words";

/**
 * Phase SC-22d — the month's pay at a glance: what each member earned,
 * what was added, taken off and paid, and what is owed as of today.
 */

type SheetKind = "statement" | "payment" | "advance" | "adjustment" | "terms";

interface Selected {
  kind: SheetKind;
  payeeId: string | null;
  name: string;
  owedPaise: number;
}

function CloseMonthDialog({
  open,
  onOpenChange,
  year,
  month,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  year: number;
  month: number;
}) {
  const close = useCloseMonth();
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const run = async (finalize: boolean) => {
    setError(null);
    try {
      const res = await close.mutateAsync({ year, month, finalize });
      const skipped = res.skipped_members.length
        ? ` ${res.skipped_members.length} without terms were left out.`
        : "";
      toast.success(
        `${finalize ? "Closed" : "Drafted"} ${monthLabel(year, month)} · ${res.payslip_count} payslips · ${formatPaise(res.net_total_paise)}.${skipped}`,
      );
      onOpenChange(false);
    } catch (err) {
      setError(errorMessage(err, "Couldn't close the month."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Close {monthLabel(year, month)}</DialogTitle>
          <DialogDescription>
            Builds the month&apos;s pay run from attendance, additions and advance recoveries. A
            draft can still change; closing locks it into your profit &amp; loss.
          </DialogDescription>
        </DialogHeader>
        {error ? <ErrorBanner message={error} /> : null}
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" disabled={close.isPending} onClick={() => run(false)}>
            Save as draft
          </Button>
          <Button disabled={close.isPending} onClick={() => run(true)}>
            {close.isPending ? (
              <Loader2 aria-hidden="true" className="size-4 animate-spin" />
            ) : null}
            Close month
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PayTab({
  year,
  month,
  canManage,
  canClose,
}: {
  year: number;
  month: number;
  canManage: boolean;
  canClose: boolean;
}) {
  const words = useTeamWords();
  const summary = useTeamPaySummary(year, month);
  const members = useTeamMembers();
  const [filter, setFilter] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = usePageSizePreference({
    scope: "team-pay-summary",
    defaultPageSize: 25,
  });
  const [selected, setSelected] = React.useState<Selected | null>(null);
  const [sheetOpen, setSheetOpen] = React.useState(false);
  const [editMember, setEditMember] = React.useState<TeamMember | null>(null);
  const [closeOpen, setCloseOpen] = React.useState(false);

  const rows = React.useMemo(() => {
    const all = summary.data?.rows ?? [];
    const q = filter.trim().toLowerCase();
    return q
      ? all.filter(
          (r) => r.name.toLowerCase().includes(q) || r.role_label.toLowerCase().includes(q),
        )
      : all;
  }, [summary.data, filter]);
  const pageRows = rows.slice(page * pageSize, page * pageSize + pageSize);
  const totals = summary.data?.totals;

  React.useEffect(() => setPage(0), [filter, year, month]);

  const open = (kind: SheetKind, row: SummaryRow | null) => {
    if (kind === "terms") {
      setEditMember(
        row ? (members.data?.items.find((m) => m.id === row.staff_payee_id) ?? null) : null,
      );
    }
    setSelected({
      kind,
      payeeId: row?.staff_payee_id ?? null,
      name: row?.name ?? "",
      owedPaise: row ? Math.max(0, row.owed_today_paise) : 0,
    });
    setSheetOpen(true);
  };

  const sheetProps = (kind: SheetKind) => ({
    open: sheetOpen && selected?.kind === kind,
    onOpenChange: (v: boolean) => setSheetOpen(v),
  });

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard
          label="Owed today"
          value={totals ? formatPaiseCompact(Math.max(0, totals.owed_today_paise)) : "—"}
          caption={`Across ${summary.data?.rows.length ?? 0} ${words.members.toLowerCase()}`}
          icon={<Wallet />}
          tone="blue"
        />
        <StatCard
          label="Paid this month"
          value={totals ? formatPaiseCompact(totals.paid_paise) : "—"}
          caption={monthLabel(year, month)}
          icon={<CircleDollarSign />}
          tone="green"
        />
        <StatCard
          label="Advances out"
          value={totals ? formatPaiseCompact(totals.advance_outstanding_paise) : "—"}
          caption="Still to be taken back"
          icon={<HandCoins />}
          tone="amber"
        />
        <StatCard
          label="Days to approve"
          value={totals ? String(totals.pending_attendance) : "—"}
          caption="Check-ins waiting"
          icon={<CalendarCheck />}
          tone="violet"
        />
      </div>

      {summary.isError ? (
        <ErrorBanner
          message={errorMessage(summary.error, "Couldn't load the pay summary.")}
          onRetry={() => summary.refetch()}
        />
      ) : null}

      <DataTable responsive="cards">
        <DataTableToolbar>
          <Input
            aria-label={`Filter ${words.members.toLowerCase()}`}
            placeholder="Filter by name or role"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-9 w-full sm:w-64"
          />
          <DataTableToolbarSpacer />
          {canClose ? (
            <Button size="sm" variant="outline" onClick={() => setCloseOpen(true)}>
              Close month
            </Button>
          ) : null}
          {canManage ? (
            <Button size="sm" onClick={() => open("terms", null)} data-testid="add-team-member">
              <UserPlus aria-hidden="true" className="size-4" />
              Add {words.member.toLowerCase()}
            </Button>
          ) : null}
        </DataTableToolbar>

        <DataTableScroll>
          <Table>
            <TableCaption className="sr-only">
              Pay for {monthLabel(year, month)}, as of today
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead className="bg-background sticky left-0 z-[1] min-w-44 shadow-[inset_-1px_0_0_hsl(var(--border))]">
                  {words.member}
                </TableHead>
                <TableHead className="text-right">Earned</TableHead>
                <TableHead className="text-right">Added</TableHead>
                <TableHead className="text-right">Taken off</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Owed today</TableHead>
                <TableHead className="text-right">Advance left</TableHead>
                <TableHead className="w-[1%] text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            {summary.isLoading ? (
              <DataTableSkeleton rows={5} columns={8} />
            ) : (
              <TableBody>
                {pageRows.map((row) => (
                  <TableRow
                    key={row.staff_payee_id}
                    data-testid={`team-pay-row-${row.staff_payee_id}`}
                    className="hover:bg-muted/40 transition-colors duration-[120ms]"
                  >
                    <TableCell className="bg-background sticky left-0 z-[1] shadow-[inset_-1px_0_0_hsl(var(--border))]">
                      <button
                        type="button"
                        className="hover:text-primary focus-visible:ring-ring rounded-sm text-left focus-visible:outline-none focus-visible:ring-1"
                        onClick={() => open("statement", row)}
                      >
                        <span className="block font-medium">{row.name}</span>
                        <span className="text-muted-foreground block text-xs">
                          {row.role_label} · {PAY_BASIS_LABELS[row.pay_basis]}
                        </span>
                      </button>
                      {!row.is_active ? (
                        <StatusPill status="inactive" label="Inactive" className="mt-1" />
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatPaise(row.earned_paise)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatPaise(row.additions_paise)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatPaise(row.deductions_paise + row.recovery_paise)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatPaise(row.paid_paise)}
                    </TableCell>
                    <TableCell className="text-right font-medium tabular-nums">
                      {formatPaise(row.owed_today_paise)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatPaise(row.advance_outstanding_paise)}
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-8"
                            aria-label={`Actions for ${row.name}`}
                          >
                            <MoreHorizontal aria-hidden="true" className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48">
                          <DropdownMenuItem onSelect={() => open("statement", row)}>
                            View statement
                          </DropdownMenuItem>
                          {canManage ? (
                            <>
                              <DropdownMenuItem onSelect={() => open("payment", row)}>
                                Record payment
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => open("advance", row)}>
                                Give advance
                              </DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => open("adjustment", row)}>
                                Add incentive or deduction
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onSelect={() => open("terms", row)}>
                                Edit terms
                              </DropdownMenuItem>
                            </>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            )}
          </Table>
        </DataTableScroll>

        {!summary.isLoading && rows.length === 0 ? (
          <DataTableEmpty
            icon={<Users aria-hidden="true" />}
            title={filter ? "No one matches" : `No ${words.members.toLowerCase()} yet`}
            description={
              filter
                ? "Try another name or role."
                : "Add the people you pay. Their pay is worked out from their terms and attendance."
            }
            action={
              canManage && !filter ? (
                <Button size="sm" onClick={() => open("terms", null)}>
                  <UserPlus aria-hidden="true" className="size-4" />
                  Add {words.member.toLowerCase()}
                </Button>
              ) : null
            }
          />
        ) : null}

        {!summary.isLoading && rows.length > 0 ? (
          <DataTableFooter>
            <span className="text-muted-foreground text-sm tabular-nums">
              {rows.length}{" "}
              {rows.length === 1 ? words.member.toLowerCase() : words.members.toLowerCase()}
            </span>
            <DataTablePagination
              className="ml-auto"
              page={page + 1}
              pageSize={pageSize}
              totalRows={rows.length}
              isFetching={summary.isFetching}
              onPageChange={(next) => setPage(Math.max(0, next - 1))}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(0);
              }}
              testIdPrefix="team-pay-pagination"
            />
          </DataTableFooter>
        ) : null}
      </DataTable>

      <StatementSheet
        {...sheetProps("statement")}
        payeeId={selected?.payeeId ?? null}
        memberName={selected?.name ?? ""}
        year={year}
        month={month}
        canManage={canManage}
      />
      {canManage ? (
        <>
          <PaymentSheet
            {...sheetProps("payment")}
            payeeId={selected?.payeeId ?? null}
            memberName={selected?.name ?? ""}
            owedPaise={selected?.owedPaise ?? 0}
          />
          <AdvanceSheet
            {...sheetProps("advance")}
            payeeId={selected?.payeeId ?? null}
            memberName={selected?.name ?? ""}
          />
          <AdjustmentSheet
            {...sheetProps("adjustment")}
            payeeId={selected?.payeeId ?? null}
            memberName={selected?.name ?? ""}
          />
          <MemberTermsSheet {...sheetProps("terms")} member={editMember} />
        </>
      ) : null}
      {canClose ? (
        <CloseMonthDialog open={closeOpen} onOpenChange={setCloseOpen} year={year} month={month} />
      ) : null}
    </div>
  );
}
